import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Sparkles, KeyRound, IdCard, Copy, Users, Award, Package, ArrowRight, PartyPopper } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * Congratulations / first-login modal.
 * Fetches /dist/welcome-info; if `is_first_login`, opens automatically.
 * On dismiss, POSTs /dist/welcome-ack so it never reappears.
 */
export default function WelcomeModal() {
  const [info, setInfo] = useState(null);
  const [open, setOpen] = useState(false);
  const [ack, setAck] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/dist/welcome-info")
      .then((r) => {
        if (!alive) return;
        setInfo(r.data);
        if (r.data?.is_first_login) setOpen(true);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const close = async () => {
    if (ack || !info) { setOpen(false); return; }
    setAck(true);
    try {
      await api.post("/dist/welcome-ack");
    } catch { /* non-blocking */ }
    setOpen(false);
  };

  const copyCode = () => {
    if (!info?.user_code) return;
    navigator.clipboard.writeText(info.user_code);
    toast.success("IBA ID copied");
  };

  if (!open || !info) return null;

  const side = info.placement?.side === "L" ? "Left" : info.placement?.side === "R" ? "Right" : "—";

  return (
    <div
      data-testid="welcome-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-navy/60 backdrop-blur-sm animate-in fade-in duration-300"
      onClick={close}
    >
      <div
        className="relative w-full max-w-md bg-white rounded-sm border border-gold/40 shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300 my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header banner */}
        <div className="relative bg-gradient-to-br from-royal via-royal-deep to-navy px-5 py-5 text-white overflow-hidden">
          <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-gold/20 blur-2xl" />
          <div className="absolute -bottom-6 -left-6 w-24 h-24 rounded-full bg-rose/20 blur-2xl" />
          <button
            onClick={close}
            data-testid="welcome-modal-close"
            className="absolute top-2 right-2 text-white/70 hover:text-white p-1.5 rounded-sm hover:bg-white/10 transition-colors"
            aria-label="Close welcome"
          >
            <X size={16} />
          </button>
          <div className="relative flex items-start gap-3">
            <div className="w-11 h-11 grid place-items-center bg-gold/20 border border-gold/40 rounded-sm shrink-0">
              <PartyPopper size={20} className="text-gold" />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.3em] text-gold/90">Welcome aboard</div>
              <h2 className="font-serif text-2xl leading-tight mt-0.5">
                Congratulations, <span className="text-gold">{(info.name || "Associate").split(" ")[0]}</span>!
              </h2>
              <p className="text-[12px] text-white/75 mt-1.5 leading-relaxed">
                You&apos;re now an IBA of Samara Wiz — save these details safely.
              </p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="px-5 py-4">
          {/* Highlight card: IBA ID */}
          <div
            data-testid="welcome-iba-id"
            className="flex items-center justify-between gap-3 bg-gradient-to-r from-gold/10 to-ivory border border-gold/30 rounded-sm px-4 py-3 mb-4"
          >
            <div className="min-w-0">
              <div className="text-[9px] uppercase tracking-widest text-navy/50">Your IBA ID</div>
              <div className="font-serif text-xl text-royal mt-0.5 font-semibold tracking-wide truncate">
                {info.user_code}
              </div>
            </div>
            <button
              onClick={copyCode}
              data-testid="welcome-copy-code"
              className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1.5 bg-royal text-white hover:bg-royal-deep rounded-sm shrink-0"
            >
              <Copy size={11} /> Copy
            </button>
          </div>

          {/* Detail grid — 2 cols, tighter */}
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-[12px]">
            <Detail icon={IdCard} label="Name" value={info.name} />
            <Detail icon={Users} label="Sponsor" value={info.sponsor?.code ? `${info.sponsor.name || "—"} (${info.sponsor.code})` : "—"} />
            <Detail icon={Users} label="Placement" value={info.placement?.parent_code ? `${info.placement.parent_name || "—"} (${info.placement.parent_code})` : "—"} />
            <Detail icon={ArrowRight} label="Side" value={side} />
            <Detail icon={Award} label="Rank" value={info.rank?.label || "Member"} />
            <Detail icon={Package} label="Package" value={info.package?.active ? (info.package?.code || "Active") : "Not activated"} />
            <Detail icon={KeyRound} label="Email" value={info.email} />
            <Detail icon={KeyRound} label="Mobile" value={info.mobile || "—"} />
          </div>

          {/* Security notice */}
          <div className="mt-4 flex items-start gap-2 bg-cream/60 border border-gold/25 rounded-sm px-3 py-2">
            <Sparkles size={13} className="text-gold-deep shrink-0 mt-0.5" />
            <p className="text-[11.5px] text-navy/75 leading-relaxed">
              <strong className="text-navy">Tip:</strong> Change your temporary password to secure your income wallet.
            </p>
          </div>

          {/* CTAs */}
          <div className="mt-4 flex flex-wrap items-center gap-2 pt-3 border-t border-line">
            <Link
              to="/password-reset"
              onClick={close}
              data-testid="welcome-change-password"
              className="inline-flex items-center gap-1.5 bg-royal text-white hover:bg-royal-deep px-3.5 py-2 text-[12px] rounded-sm"
            >
              <KeyRound size={13} /> Change Password
            </Link>
            <Link
              to="/welcome"
              onClick={close}
              data-testid="welcome-view-letter"
              className="inline-flex items-center gap-1.5 border border-royal/25 text-royal hover:bg-royal/5 px-3.5 py-2 text-[12px] rounded-sm"
            >
              <IdCard size={13} /> Welcome Letter
            </Link>
            <button
              onClick={close}
              data-testid="welcome-continue"
              className="ml-auto inline-flex items-center gap-1 text-[12px] text-navy/70 hover:text-navy px-2 py-2"
            >
              Continue <ArrowRight size={12} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Detail({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-2 border-b border-line/70 pb-1.5">
      <Icon size={12} className="text-gold-deep shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-[9px] uppercase tracking-widest text-navy/50">{label}</div>
        <div className="text-navy truncate text-[12px]">{value || "—"}</div>
      </div>
    </div>
  );
}
